/**
 * The left panel of figure S4 — "Your inventions" — in the figure's order:
 *
 *   Your inventions
 *   (everything here is yours)
 *   ■ Aurora Battery Cathode                12 docs
 *   ■ Solid-State Membrane                   8 docs
 *   …
 *   A share lets you read a project; it isn't consent to compute across it.
 *   ✉ Shared with you · …            (listed, never plotted)
 *   🔗 Shared in · …                 (a disclosure source, at its rung)
 *
 * The swatch colour is the SAME projectTagColor the atlas plots the invention
 * with, so the roster and the map can never disagree about which shape is
 * which. Clicking a row focuses that invention on the map — real behaviour,
 * not decoration.
 *
 * Nobody else's NAME is ever rendered here. A shared-in project is listed by
 * its own title and its rung, never by the person or organisation behind it,
 * which is the own-scope privacy rule the story states in terms.
 */
import { projectTagColor } from '../../lib/whitespace'
import SharedWithYouChip from './SharedWithYouChip'
import { sharedInChipText, sharedInChipTooltip, type SharedInPortfolio } from './sharedInPortfolios'
import type { Project } from '../../types'
import type { PortfolioEntitlementSharedInSource } from '../../types/whitespace'

export interface OwnInventionRosterProps {
  /** The inventions the caller owns, in the order the atlas colours them. */
  owned: Project[]
  /** Ids the atlas actually plots — drives the swatch colour scale. */
  colourScaleIds: string[]
  /** The colour of an invention on the lens being shown (the Sector lens colours by sector).
   * Absent: the project tag colour on colourScaleIds, exactly as before. */
  colourFor?: (id: string) => string
  /** Projects someone else owns and has shared with the caller. */
  sharedIn: Project[]
  /** Disclosure-shared sources from GET /whitespace/portfolio?scope=own. */
  sharedInSources?: PortfolioEntitlementSharedInSource[]
  /** Portfolios the caller has shared their OWN inventions into, rolled up from
   * the real per-project grants. */
  sharedInPortfolios?: SharedInPortfolio[]
  focusId: string | null
  onFocus: (projectId: string) => void
  /** The signed-in caller's own id, so each share chip can read the caller's
   * OWN permission on that project — never anybody else's grant. */
  callerUserId?: string
}

export const OWN_SHARE_RULE_LINE =
  "A share lets you read a project; it isn't consent to compute across it."

export default function OwnInventionRoster({
  owned,
  colourScaleIds,
  colourFor,
  sharedIn,
  sharedInSources = [],
  sharedInPortfolios = [],
  focusId,
  onFocus,
  callerUserId,
}: OwnInventionRosterProps) {
  return (
    <div data-part="OwnInventionRoster">
      <p className="text-[12.5px] font-bold text-gray-900">Your inventions</p>
      <p className="mb-2 text-[11px] text-gray-500">(everything here is yours)</p>

      {owned.length === 0 ? (
        <p className="rounded-lg border border-gray-200 bg-gray-50 px-3 py-2 text-[12px] text-gray-600">
          You do not own any inventions yet. Create one and it is drawn here alongside the rest of
          your set.
        </p>
      ) : (
        <ul className="list-none">
          {owned.map((p) => {
            const focused = p.project_id === focusId
            return (
              <li key={p.project_id}>
                <button
                  type="button"
                  onClick={() => onFocus(p.project_id)}
                  aria-pressed={focused}
                  className={`flex w-full items-center gap-2 rounded-lg border-l-2 py-1.5 pl-1 pr-1.5 text-left transition-colors
                    ${focused ? 'border-iflow-500 bg-iflow-50' : 'border-transparent hover:bg-iflow-50'}`}
                >
                  <span
                    aria-hidden
                    className="h-3 w-3 shrink-0 rounded"
                    style={{ background: (colourFor ?? ((id: string) => projectTagColor(id, colourScaleIds)))(p.project_id) }}
                  />
                  <span className="min-w-0 flex-1 truncate text-[12px] font-semibold text-gray-900">{p.name}</span>
                  <span className="shrink-0 text-[11px] text-gray-500">
                    {p.document_count} {p.document_count === 1 ? 'doc' : 'docs'}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      )}

      <p className="mt-2.5 border-t border-gray-200 pt-2 text-[11px] leading-relaxed text-gray-500">
        {OWN_SHARE_RULE_LINE}
      </p>

      {sharedIn.map((p) => (
        <SharedWithYouChip key={p.project_id} project={p} callerUserId={callerUserId} />
      ))}

      {sharedInSources.map((s) => (
        <p
          key={s.source_project_id}
          data-part="SharedInSourceChip"
          title="A disclosure source shared in at its rung — the ladder never releases a position"
          className="mt-1.5 rounded-md border border-iflow-200 bg-iflow-50 px-2 py-1 text-[11px] text-iflow-800"
        >
          🔗 Shared in · rung {s.disclosure_level} · {s.name ?? 'Untitled source'}
        </p>
      ))}

      {sharedInPortfolios.map((p) => (
        <p
          key={p.aggregator_id}
          data-part="SharedInPortfolioChip"
          title={sharedInChipTooltip(p)}
          className="mt-1.5 rounded-md border border-violet-300 bg-violet-50 px-2 py-1 text-[11px] text-violet-900"
        >
          {sharedInChipText(p)}
        </p>
      ))}

      {sharedIn.length === 0 && sharedInSources.length === 0 && (
        <p className="mt-1.5 text-[11px] text-gray-500">Nothing has been shared with you yet.</p>
      )}
    </div>
  )
}
